<template>
  <section>
    <errors-list :errorMessage="errorMessage" />

    <v-data-table
      :headers="translatedHeaders"
      :items="invoices"
      :loading="loading"
      :rows-per-page-items="rowsPerPageItems"
      :total-items="paginationDataContainer.meta.totalItems"
      :pagination.sync="pagination"
      :disable-initial-sort="true"
    >
      <v-progress-linear slot="progress" color="blue" indeterminate></v-progress-linear>
      <template slot="items" slot-scope="props">
        <tr>
          <!-- Status column is a custom component, not using GridCell for it -->
          <td class="align-center layout pr-0">
            <v-icon class="mr-1" :color="getStatusColor(props.item.status)">
              {{ getStatusIcon(props.item.status) }}
            </v-icon>
            <v-flex class="capitalize">{{ props.item[translatedHeaders[0].value] }}</v-flex>
          </td>

          <grid-cell
            :header="header"
            :row="props.item"
            v-for="(header, index) in translatedHeaders.slice(1)"
            :key="index"
            @action="onAction"
          ></grid-cell>
        </tr>
      </template>
    </v-data-table>

    <v-dialog v-model="modal" persistent lazy full-width width="390px">
      <invoice-form
        :key="selectedInvoice && selectedInvoice.id"
        :invoice="selectedInvoice || {}"
        @close-modal="closeModal"
      />
    </v-dialog>
  </section>
</template>

<script>
import moment from 'moment';
import ErrorsList from '../../components/ErrorsList';
import GridCell from '../../components/GridCell';
import { getErrorMessage } from '../../errorHandlers';
import { defaultDateFormatter, serializeQuery } from '../../utils';
import { INVOICE_APPROVED, INVOICE_DECLINED, INVOICE_PDF_GENERATED } from '../../wsEventTypes';
import InvoiceForm from './InvoiceForm';

export const PdfStatusEnum = {
  generated: 'generated',
};

export const defaultHeaders = [
  {
    text: '@invoicesList/statusColumnHeader',
    value: 'status',
  },
  {
    text: '@invoicesList/typeColumnHeader',
    align: 'left',
    value: 'type',
  },

  {
    text: '@invoicesList/sumColumnHeader',
    value: 'sum',
  },
  {
    text: '@invoicesList/paidColumnHeader',
    value: 'paid',
  },
  {
    text: '@invoicesList/fromColumnHeader',
    value: 'from',
    format: defaultDateFormatter,
    sortable: false,
  },
  {
    text: '@invoicesList/toColumnHeader',
    value: 'to',
    format: defaultDateFormatter,
    sortable: false,
  },
  {
    text: '@invoicesList/createdAtColumnHeader',
    value: 'createdAt',
    format: defaultDateFormatter,
  },
  {
    text: '@invoicesList/processedAtColumnHeader',
    value: 'processedAt',
    format: defaultDateFormatter,
  },
  {
    text: '',
    align: 'right',
    sortable: false,
    actions: (row) =>
      row.pdfStatus === PdfStatusEnum.generated
        ? [{ icon: 'cloud_download', href: `/api/invoices/${row.id}/pdf` }]
        : [],
    value: '&nbsp;',
  },
];

const defaultPaginationData = {
  data: [],
  meta: {
    totalItems: 0,
  },
};

export default {
  components: { GridCell, InvoiceForm, ErrorsList },

  props: {
    filters: {
      type: Object,
      default: () => ({}),
    },
    headers: {
      type: Array,
      default: () => defaultHeaders,
    },
    pickerDate: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      params: {
        from: moment()
          .startOf('month')
          .startOf('day')
          .format(),
      },
      items: [],
      selectedInvoice: null,
      rowsPerPageItems: [10, 15, 50, 100],
      modal: false,
      loading: false,
      errorMessage: '',
      paginationDataContainer: { ...defaultPaginationData },
      pagination: {
        descending: true,
        sortBy: 'createdAt',
        totalItems: 0,
        rowsPerPage: 10,
        page: 1,
      },
    };
  },

  watch: {
    filters() {
      this.fetchData();
    },
    pagination() {
      this.fetchData();
    },
    pickerDate() {
      this.fetchData();
    },
  },

  created() {
    this.fetchData();
  },

  computed: {
    translatedHeaders() {
      return Array.isArray(this.headers)
        ? this.headers.map((h) => ({
            ...h,
            text: this.$t(h.text),
          }))
        : [];
    },
    query() {
      const skip = (this.pagination.page - 1) * this.pagination.rowsPerPage;
      const take = this.pagination.rowsPerPage;
      const query = {
        ...this.filters,
        skip,
        take,
      };

      if (this.pagination.sortBy) {
        query.sortBy = `invoice.${this.pagination.sortBy}`;
        query.sortOrder = this.pagination.descending ? 'DESC' : 'ASC';
      } else {
        delete query.sortBy;
        delete query.sortOrder;
      }

      if (!query.from) {
        query.from = '1970-01-01T00:00:00.000Z';
      }
      return query;
    },
    invoices() {
      if (!Array.isArray(this.paginationDataContainer.data)) {
        return [];
      }

      return this.paginationDataContainer.data.map((invoice) => {
        if (!Array.isArray(invoice.debts)) {
          return invoice;
        }

        const debtStarts = invoice.debts.map((debt) => moment(debt.periodFrom));
        const debtEnds = invoice.debts.map((debt) => moment(debt.periodTill));
        const from = moment.min(debtStarts);
        const to = moment.max(debtEnds);

        return { ...invoice, from, to };
      });
    },
  },

  methods: {
    onAction({ row, action }) {
      if (!action) {
        this.selectedInvoice = row;
        this.modal = true;
      }
    },

    async closeModal(isSent = false) {
      this.modal = false;

      if (isSent) {
        await this.fetchData(true);
        this.$emit('invoice-details-updated');
      }
      this.selectedInvoice = null;
    },

    async fetchData(force) {
      const qs = serializeQuery(this.query);

      if (!force && (this.lastQueryString === qs || this.loading)) {
        return;
      }

      try {
        this.loading = true;
        this.$emit('data-fetched', []);
        this.$emit('beforeRequest', this.query);

        const { data: invoices = [] } = await this.$http.get(`/api/invoices?${qs}`);

        this.paginationDataContainer = {
          data: invoices.data.map((invoice) => ({
            ...invoice,
            sum: Number(invoice.sum.toFixed(2)),
          })),
          meta: {
            totalItems: invoices.meta.total,
          },
        };
        this.$emit('data-fetched', invoices.data.map((el) => el.id));
        this.lastQueryString = qs;
        this.errorMessage = '';
      } catch (e) {
        this.errorMessage = getErrorMessage(e);
      } finally {
        this.loading = false;
      }
    },

    getStatusIcon(status) {
      if (['new', 'pending'].includes(status)) {
        return 'watch_later';
      } else if (['approved', 'processed'].includes(status)) {
        return 'check_circle';
      } else if (status === 'declined') {
        return 'error';
      } else {
        return 'help';
      }
    },

    getStatusColor(status) {
      if (['new', 'pending'].includes(status)) {
        return 'yellow darken-3';
      } else if (['approved', 'processed'].includes(status)) {
        return 'light-green darken-3';
      } else if (status === 'declined') {
        return 'red darken-2';
      } else {
        return 'grey';
      }
    },
  },

  sockets: {
    insuranceSubscribe: function(data) {
      const eventTypes = [INVOICE_APPROVED, INVOICE_DECLINED, INVOICE_PDF_GENERATED];

      if (
        data.meta &&
        eventTypes.includes(data.type) &&
        this.items.find((el) => el.contractId === data.meta.contractId)
      ) {
        this.fetchData(true);
      }
    },
  },
};
</script>

<style scoped>
>>> td:not(.layout) {
  cursor: pointer;
  white-space: pre;
}
</style>
